/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
const pendo = window.pendo;

const clientIdRegex = /\/c\/(\d+)/;

import removeClientFromPathname from './removeClientFromPathname';
import { configurationMapper } from '../constants/configurationStatus';
import { valuationMapper } from '../constants/valuationStatus';
import { MVC_REFRESH } from '../constants/config';

/**
 * Gets the user information from the API stored on localStorage, it also checks
 * the timestamp to see if the data is expired
 * @returns {Object} user
 */
const getUserAndClientsFromStorage = () => {
    if (/^\/pacing-graph\/[0-9]+/g.test(window.location.pathname))
        return { user: {}, clients: {} };
    const userString = localStorage.getItem('mvc-user');
    const clientsString = localStorage.getItem('mvc-user-clients');

    const user = JSON.parse(userString);
    const clients = JSON.parse(clientsString);

    const sessionExpired = user ? Date.now() > user.timestamp : true;

    if (userString && !sessionExpired) {
        const matches = window.location.pathname.match(clientIdRegex);
        const urlClientId = matches && matches[1] && parseInt(matches[1], 10);

        let clientExists =
            Array.isArray(user.clients) &&
            user.clients.some(c => c.id === urlClientId);

        let clientId = urlClientId;

        // If there is not clientId in the URL, default to the one in localStorage
        if (urlClientId === null) {
            clientId = user.selectedClientId;
            clientExists = true;
        }

        // If the urlClientId doesn't exist under the logged user, select first available client
        if (!clientExists) {
            clientId = user.clients[0].id;
            const basename = __BASEPATH__ ? `/${__BASEPATH__}` : '';
            const { origin, pathname, search } = window.location;

            // Handle special case where twitter-api won't let us preserve client-id
            const twitterRedirectClientId = localStorage.getItem(
                'twitter-auth-client-id',
            );

            if (twitterRedirectClientId) {
                localStorage.removeItem('twitter-auth-client-id');
                clientId = twitterRedirectClientId;
            }

            const newUrl =
                origin + basename + `/c/${clientId}` + pathname + search;
            window.location.replace(newUrl);
            console.log('Redirecting to', newUrl);
        }

        const shouldSaveNewUserInfo = user.selectedClientId !== clientId;

        user.selectedClientId = clientId;
        user.selectedUserClientId = `${user.id}-${clientId}`;

        if (shouldSaveNewUserInfo) {
            const updatedUser = JSON.stringify(user);
            localStorage.setItem('mvc-user', updatedUser);
        }

        const client =
            user.clientsById && clientId != null
                ? user.clientsById[clientId]
                : null;
        if (!client) {
            return { user: {}, clients: {} };
        }

        removeClientFromPathname(location.pathname);

        const is_analyst = user.roles.includes('ROLE_ANALYST');

        // Setup pendo
        const userId =
            user.gumgumUser || is_analyst ? `${user.id}_internal` : user.id;
        const userFullname = `${user.firstName} ${user.lastName}`;
        const accountManagers =
            Array.isArray(client.accountManagers) &&
            client.accountManagers.map(am => am.email);
        const valuationStatus =
            valuationMapper(client.valuationStatusId) &&
            valuationMapper(client.valuationStatusId).name;
        const configStatus =
            configurationMapper(client.configStatusId) &&
            configurationMapper(client.configStatusId).name;

        const pendoConfig = {
            visitor: {
                id: userId, // Required if user is logged in
                email: user.email, // Recommended if using Pendo Feedback, or NPS Email
                full_name: userFullname, // Recommended if using Pendo Feedback
                role: user.roles, // Optional
                // EXTRA MAPPINGS
                gumgum_user: user.gumgumUser,
                is_analyst,
                is_edge_user:
                    localStorage.getItem(`show-mvc-refresh`) === '1' &&
                    client.permissions?.includes(MVC_REFRESH),
                // You can add any additional visitor level key-values here,
                // as long as it's not one of the above reserved names.
            },
            account: {
                id: client.id, // Highly recommended
                name: client.name, // Optional
                // is_paying:       // Recommended if using Pendo Feedback
                // monthly_value:   // Recommended if using Pendo Feedback
                // planLevel:       // Optional
                // planPrice:       // Optional
                // creationDate:    // Optional
                // EXTRA MAPPINGS
                valuation_status: valuationStatus,
                configuration_status: configStatus,
                account_managers: accountManagers,
                qa_analyst:
                    client.socialQaAnalyst && client.socialQaAnalyst.email,
                valuation_start: client.valuationStart,
                valuation_end: client.valuationEnd,
                language: client.language,
                currency: client.currency && client.currency.code,
                client_permissions: client.permissions,
            },
        };
        if (pendo) pendo.initialize(pendoConfig);
        //eslint-disable-next-line no-console
        else console.log('No pendo installation found...');

        if (!__DEV__) {
            Sentry.configureScope(scope => {
                scope.setUser({
                    id: user.id,
                    email: user.email,
                    username: user.username,
                    client: client.name,
                    clientId: client.id,
                    roles: user.roles,
                });
            });
        }

        return { user, clients };
    }

    return { user: {}, clients: {} };
};

export default getUserAndClientsFromStorage;
